import {
	Button,
	Row,
	Result,
	Select,
	Divider
} from "antd";

import { UploadDocument } from '@qualitas/upload-document';

import FormLayout from "../../FormLayout/FormLayout";

import { ContractAPI } from "../../../Api/ContractAPI";
import { InvestorAPI } from "../../../Api/InvestorAPI";
import { DocumentsAPI } from "../../../Api/DocumentsAPI";

import { usePhysicalInvestorDocuments } from "../../../Hooks/usePhysicalFormDocuments/usePhysicalInvestorDocuments";

import { IPhysicalInvestorDocuments } from "./IPhysicalInvestorDocuments";
import { InvestorDTO } from "../../../Models";

import { FileTypes } from "../../../Assets/Enums";
import { getLocalizedPhysicalSourceValue } from "../../../Assets/OptionsTranslations";

import "./PhysicalInvestorDocuments.scss";

const PhysicalInvestorDocuments = ({
	baseURL,
	baseURLExternal,
	translations,
	language,
	clientId,
	unlockNextStep,
	moduleRole,
	isReadOnly = false,
}: IPhysicalInvestorDocuments) => {
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);
	const investorsAPI = InvestorAPI(baseURL, baseURLExternal, clientId);
	const documentsAPI = DocumentsAPI(baseURL, clientId);
	
	const {
		loading,
		noContent,
		contractData,
		investors,
		documentsData,
		errors,
		handleUpdateIdDocument,
		handleUpdateFundSourceDocument,
		saveData,
		areAllDocumentsUploaded,
		getFundsOriginOptions,
		handleDocumentTypeChange,
		handleUpdateDocument,
		getAllUniqueFundSources,
		isMandatoryInvestorDocumentMissing,
		isMandatoryBankCertificateMissing,
		isMandatoryFundSourceDocumentMissing,
	} = usePhysicalInvestorDocuments({
		translations,
		contractAPI,
		investorsAPI,
		documentsAPI,
		unlockNextStep,
	});

	return (
		<>
			{!noContent ? (
				<>
					<FormLayout
						translations={translations}
						loading={loading}
						title={translations.documentsTitle}
						subTitle={translations.documentsSubtitle}
						onClick={saveData}
						contractId={contractData.id}
						nextStep={unlockNextStep}
						enableNextStep={areAllDocumentsUploaded()}
						forceEnableNextStep={isReadOnly}
						confirm={contractData.id ? null : translations.confirmContract }
						disabledSaveButton={false}
						hideSaveButton={isReadOnly}
					>
						<Row className="documents-list">
							{ investors.map((investor: InvestorDTO, investorIndex: number) => (
								<div className="subitem-line">
									<h3>
										{translations.datosFirmante} {investor.name} {investor.lastName} {investor.secondLastName}
									</h3>
									<Row justify="start" className="documents-row full-width">
										<UploadDocument
											baseURL={baseURL}
											clientId={clientId}
											moduleRole={moduleRole}
											translations={{
												label: translations[investor.documentType?.toLowerCase() as keyof typeof translations] || investor.documentType,
												uploadDocument: translations.uploadDocument,
												uploadDocumentTooltip: translations.allowedFileFormats,
												selectFiles: translations.selectFiles,
												changeFile: translations.changeFile,
											}}
											mandatory={true}
											oneRow={false}
											uploadedFile={documentsData.investorDocuments[investorIndex]?.idDocument?.url || ""}
											disabled={isReadOnly}
											error={isMandatoryInvestorDocumentMissing(investorIndex, 'idDocument')}
											shouldRunOcr={false}
											ocrFields={[]}
											updateFileUrl={handleUpdateIdDocument(investorIndex, investor.documentType!)}
											fileName={`${translations[investor.documentType?.toLowerCase() as keyof typeof translations]?.replace(' ', '-')}`}
											maxDocuments={3}
											allowMultipleDocuments
										/>
									</Row>
									{
										investor.countryTaxResidence !== "España" 
											? (
												<>
													{
														!investor.american
															? (
																<Row justify="start" className="documents-row full-width">
																	<UploadDocument
																		baseURL={baseURL}
																		clientId={clientId}
																		moduleRole={moduleRole}
																		translations={{
																			label: translations.formW8Short,
																			uploadDocument: translations.uploadDocument,
																			uploadDocumentTooltip: translations.allowedFileFormats,
																			selectFiles: translations.selectFiles,
																			changeFile: translations.changeFile,
																		}}
																		mandatory={true}
																		oneRow={false}
																		uploadedFile={documentsData.investorDocuments[investorIndex]?.w8BEN?.url}
																		disabled={isReadOnly}
																		error={isMandatoryInvestorDocumentMissing(investorIndex, 'w8BEN')}
																		shouldRunOcr={false}
																		ocrFields={[]}
																		updateFileUrl={handleUpdateDocument(investorIndex, 'w8BEN')}
																	/>
																</Row>
															) : null
													}
													{
														investor.american
															? (
																<Row justify="start" className="documents-row full-width">
																	<UploadDocument
																		baseURL={baseURL}
																		clientId={clientId}
																		moduleRole={moduleRole}
																		translations={{
																			label: translations.formW9Short,
																			uploadDocument: translations.uploadDocument,
																			uploadDocumentTooltip: translations.allowedFileFormats,
																			selectFiles: translations.selectFiles,
																			changeFile: translations.changeFile,
																		}}
																		mandatory={true}
																		oneRow={false}
																		uploadedFile={documentsData.investorDocuments[investorIndex]?.w9?.url}
																		disabled={isReadOnly}
																		error={isMandatoryInvestorDocumentMissing(investorIndex, 'w9')}
																		shouldRunOcr={false}
																		ocrFields={[]}
																		updateFileUrl={handleUpdateDocument(investorIndex, 'w9')}
																	/>
																</Row>
															) : null
													}
													<Row justify="start" className="documents-row full-width">
														<UploadDocument
															baseURL={baseURL}
															clientId={clientId}
															moduleRole={moduleRole}
															translations={{
																label: translations.tin,
																uploadDocument: translations.uploadDocument,
																uploadDocumentTooltip: translations.allowedFileFormats,
																selectFiles: translations.selectFiles,
																changeFile: translations.changeFile,
															}}
															mandatory={false}
															oneRow={false}
															uploadedFile={documentsData.investorDocuments[investorIndex]?.tin?.url}
															disabled={isReadOnly}
															error={false}
															shouldRunOcr={false}
															ocrFields={[]}
															updateFileUrl={handleUpdateDocument(investorIndex, 'tin')}
														/>
													</Row>
													<Row justify="start" className="documents-row full-width">
														<UploadDocument
															baseURL={baseURL}
															clientId={clientId}
															moduleRole={moduleRole}
															translations={{
																label: translations.taxResidenceCertificate,
																uploadDocument: translations.uploadDocument,
																uploadDocumentTooltip: translations.allowedFileFormats,
																selectFiles: translations.selectFiles,
																changeFile: translations.changeFile,
															}}
															mandatory={true}
															oneRow={false}
															uploadedFile={documentsData.investorDocuments[investorIndex]?.taxResidenceCertificate?.url}
															disabled={isReadOnly}
															error={isMandatoryInvestorDocumentMissing(investorIndex, 'taxResidenceCertificate')}
															shouldRunOcr={false}
															ocrFields={[]}
															updateFileUrl={handleUpdateDocument(investorIndex, 'taxResidenceCertificate')}
														/>
													</Row>
												</>
											) : null
									}
									<Divider/>
								</div>
							))}
							<div className="subitem-line">
								<h3>{translations.fundSourcesPFTitle}</h3>
								{
									getAllUniqueFundSources().map((source: string, sourceIndex: number) => {
										const localizedSource = getLocalizedPhysicalSourceValue(source, language);
										const contractErrors = errors?.contract || {};
										const sourceErrors = contractErrors[source] || {};
										const fundsOriginDoc = documentsData.fundsOriginDocuments?.find(
											(doc) => doc.type === FileTypes[source] || (!doc.description && doc.type === FileTypes[source])
										) || null;
										return (
											<Row justify="start" className="documents-row" key={sourceIndex}>
												<UploadDocument
													className="document-uploader"
													baseURL={baseURL}
													clientId={clientId}
													moduleRole={moduleRole}
													translations={{
														label: localizedSource,
														uploadDocument: translations.uploadDocument,
														uploadDocumentTooltip: translations.allowedFileFormats,
														selectFiles: translations.selectFiles,
														changeFile: translations.changeFile,
													}}
													mandatory={true}
													oneRow={false}
													uploadedFile={fundsOriginDoc?.url}
													disabled={isReadOnly}
													error={Boolean(sourceErrors.fundsOriginFileNotUploaded || isMandatoryFundSourceDocumentMissing(source))}
													shouldRunOcr={false}
													ocrFields={[]}
													updateFileUrl={handleUpdateFundSourceDocument(source)}
													fileName={`${translations.fundSourcesPJTitle.replace(' ', '-')}_${source.replace(' ', '-')}`}
													maxDocuments={3}
													allowMultipleDocuments
												/>
												<div className="document-type-select">
													<label className="required">
														{translations.documentType}
													</label>
													<Select
														style={{ width: "100%" }}
														showSearch
														allowClear={false}
														value={fundsOriginDoc?.description || undefined}
														maxTagCount="responsive"
														onChange={handleDocumentTypeChange(source)}
														status={sourceErrors.fundsOriginTypeNotSelected ? 'error' : undefined}
														options={getFundsOriginOptions(source)}
														disabled={isReadOnly || !fundsOriginDoc?.url}
													/>
												</div>
											</Row>
										);
									})
								}
								<Divider/>
								<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.bankCertificate,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={true}
										oneRow={false}
										uploadedFile={documentsData.bankCertificate?.url}
										disabled={isReadOnly}
										error={isMandatoryBankCertificateMissing()}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument(0, 'bankCertificate')}
									/>
								</Row>
							</div>
						</Row>
					</FormLayout>
				</>
			) : (
				<div className="no-content-wrapper">
					<Result
						status="warning"
						title={translations.messageContrato}
						extra={
							<Button type="primary" key="console">
								{translations.inicio}
							</Button>
						}
					/>
				</div>
			)}
		</>
	);
};

export default PhysicalInvestorDocuments;
